import { createContext, lazy, Suspense, useCallback, useState } from "react";
import { Navigate, Outlet, Route, Routes } from "react-router-dom-v5-compat";
import type { SessionInfo } from "@b2bportal/auth-api";
import { DefaultHotelSlotProvider } from "@b2bportal/core-hotels";
import {
  HtsMediaRequestMetadataProvider,
  PageViewIdProvider,
} from "@b2bportal/core-media";
import { Funnel } from "@b2bportal/core-types";
import { LazyLoading } from "@b2bportal/core-ui";
import { ErrorBoundary } from "@b2bportal/core-utilities";
import { useI18nContext } from "@hopper-b2b/i18n";
import { Portal } from "@hopper-b2b/portal";
import { AgentImpersonation } from "@hopper-b2b/self-serve";

import { PATH_AUTH, PATH_MAINTENANCE } from "@hopper-b2b/utilities";
import { PATH_AGENT_IMPERSONATION } from "@hopper-b2b/utilities";
import {
  getPackageRoutes,
  HOTELS_PATH,
  ACTIVITIES_PATH,
  HOMES_PATH,
  WATCHES_PATH,
  TRAVEL_OFFERS_PATH,
  HOTEL_FUNDED_OFFERS_PATH,
  TRAVEL_DEALS_PATH,
  WALLET_PATH,
} from "@overrides/utilities";
import { HotelsConfigProvider } from "@tenant/hotels";
import { APIProvider } from "@vis.gl/react-google-maps";
import config, { environmentConfig } from "../config";
import "./App.scss";
import {
  AppTrackingProvider,
  AuthModule,
  DocumentTitleUpdater,
  HtsAssistChatSupportProvider,
  HtsAssistChatWidgetGate,
} from "./components";
import { FunnelMaintenanceGuard } from "./components/FunnelMaintenanceGuard";
import { Layout } from "./components/Layout";
import { getMaintenanceProps, Maintenance } from "./components/Maintenance";
import { MaintenanceRoute } from "./components/MaintenanceRoute";
import { HelpButton } from "./components/shared";
import { AxiosInterceptorWrapper } from "./components/shared/AxiosInterceptorWrapper";
import { useMobileAppScrollFix } from "./hooks/useMobileAppScrollFix";
import { ProtectedRoute } from "./modules/auth/ProtectedRoute";
import { useFunnelEnabled } from "./modules/experiments/src";
import { Homepage } from "./pages/Homepage";
import { HotelFundedOffersPage } from "./pages/HotelFundedOffers";
import { hotelAppConfig } from "./pages/Hotels/config";
import { TravelDealsPage } from "./pages/TravelDeals";
import { TravelOffersPage } from "./pages/TravelOffers";
const WalletApp = lazy(() =>
  import("./pages/Wallet").then((m) => ({ default: m.WalletPage }))
);
import store from "./store";
import {
  HTS_ASSIST_CHAT_PATH,
  HTS_MEDIA_CHANNEL_ID,
} from "./utilities/constants";
import { getMaintenanceMessages } from "./utilities/getMaintenanceMessages";
import { useMaintenanceMode } from "./utilities/hooks/useMaintenanceMode";

const FlightsApp = lazy(() => import("./pages/Flights"));
const HotelsApp = lazy(() => import("./pages/Hotels"));
const HomesApp = lazy(() => import("./pages/Homes"));
const TripsApp = lazy(() => import("./pages/Trips"));
const PackagesApp = lazy(() => import("./pages/Packages"));
const CarsApp = lazy(() => import("./pages/Cars"));
const ActivitiesApp = lazy(() => import("./pages/Activities"));
const DisruptionApp = lazy(() => import("./pages/DisruptionExercise"));
const WatchesApp = lazy(() => import("./pages/Watches"));

const FlightsRebookApp = lazy(() => import("./pages/FlightsRebook"));
const FlightsExchangePage = lazy(() =>
  import("./pages/FlightsExchange").then((m) => ({
    default: m.FlightsExchangePage,
  }))
);
const ConfirmationPageStandalone = lazy(() =>
  import("./modules/flight-checkout/components/ConfirmationPage").then((m) => ({
    default: m.ConfirmationPage,
  }))
);

const HotelConfirmationPageStandalone = lazy(() =>
  import("./modules/hotel-checkout/components/ConfirmationPage").then((m) => ({
    default: m.HotelConfirmationPage,
  }))
);

const CarConfirmationPageStandalone = lazy(() =>
  import("./modules/cars-checkout/components/ConfirmationPage").then((m) => ({
    default: m.CarConfirmationPage,
  }))
);

const PackageConfirmationPageStandalone = lazy(() =>
  import("./modules/package-checkout/components/ConfirmationPage").then(
    (m) => ({
      default: m.PackageConfirmationPage,
    })
  )
);

const ActivityConfirmationPageStandalone = lazy(() =>
  import("./modules/activities-checkout/components/ConfirmationPage").then(
    (m) => ({
      default: m.ActivityConfirmationPage,
    })
  )
);
const StandaloneHtsAssistChatPage = lazy(
  () => import("./components/HtsAssistChatWidget/StandaloneHtsAssistChatPage")
);

type UserInfoContext = {
  sessionInfo: SessionInfo | null;
  pointsBalance: number;
  updatePointsBalance: (pointsBalance: number) => void;
  updateSessionInfo: (sessionInfo: SessionInfo) => void;
  setError: (error: string) => void;
  error: string;
};

export const UserContext = createContext<Partial<UserInfoContext>>({});

const AppContent = ({ isSignedIn }: { isSignedIn?: boolean }) => {
  const { language } = useI18nContext();
  const {
    isFlightsEnabled,
    isStaysEnabled,
    isCarsEnabled,
    isActivitiesEnabled,
    isPackagesEnabled,
    isTripsEnabled,
    defaultFunnel,
  } = useFunnelEnabled();

  const { isMaintenanceEnabled, maintenancePayload } = useMaintenanceMode();
  // No funnel in payload = all funnels are down
  const isGlobalMaintenance =
    isMaintenanceEnabled && !maintenancePayload?.funnel;

  const { title, subtitle } = getMaintenanceMessages(
    language,
    maintenancePayload
  );

  return (
    <Routes>
      <Route path={`${PATH_AUTH}*`} element={<AuthModule />} />
      <Route path={PATH_AGENT_IMPERSONATION} element={<AgentImpersonation />} />
      <Route path={PATH_MAINTENANCE} element={<MaintenanceRoute />} />
      <Route
        path={HTS_ASSIST_CHAT_PATH}
        element={
          <ProtectedRoute>
            <Suspense fallback={<LazyLoading />}>
              <StandaloneHtsAssistChatPage />
            </Suspense>
          </ProtectedRoute>
        }
      />
      <Route
        path="/*"
        element={
          <ProtectedRoute>
            <Layout isMaintenanceMode={isGlobalMaintenance}>
              {isGlobalMaintenance ? (
                <Maintenance title={title} subtitle={subtitle} />
              ) : (
                <Routes>
                  <Route
                    key={"home"}
                    path={"/"}
                    element={
                      <FunnelMaintenanceGuard funnel={defaultFunnel}>
                        <Homepage funnel={defaultFunnel} />
                      </FunnelMaintenanceGuard>
                    }
                  />

                  {/* Air Routes */}
                  {isFlightsEnabled && (
                    <Route
                      path="/flights/*"
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Air}>
                          <Routes>
                            <Route
                              path="confirmation/:reservationId"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <ConfirmationPageStandalone />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              element={<Homepage funnel={Funnel.Air} />}
                            />
                            <Route
                              path="*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <FlightsApp />
                                </Suspense>
                              }
                            />
                            <Route
                              path="rebook/*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <FlightsRebookApp />
                                </Suspense>
                              }
                            />
                          </Routes>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}

                  {/* Hotels Routes */}
                  {isStaysEnabled && (
                    <Route
                      path={`${HOTELS_PATH}/*`}
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Lodging}>
                          <Routes>
                            <Route
                              path="confirmation/:reservationId"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <HotelConfirmationPageStandalone />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              element={<Homepage funnel={Funnel.Lodging} />}
                            />
                            <Route
                              path="*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <HotelsApp />
                                </Suspense>
                              }
                            />
                          </Routes>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}
                  {/* Vacation Rentals Routes - redirected from hotels availability */}
                  {isStaysEnabled && (
                    <Route
                      path={`${HOMES_PATH}/*`}
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Lodging}>
                          <Suspense fallback={<LazyLoading />}>
                            <HomesApp />
                          </Suspense>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}

                  {/* Cars Routes */}
                  {isCarsEnabled && (
                    <Route
                      path="/cars/*"
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Ground}>
                          <Routes>
                            <Route
                              path="confirmation/:reservationId"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <CarConfirmationPageStandalone />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              element={<Homepage funnel={Funnel.Ground} />}
                            />
                            <Route
                              path="*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <CarsApp />
                                </Suspense>
                              }
                            />
                          </Routes>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}

                  {/* Packages Routes */}
                  {isPackagesEnabled && (
                    <Route
                      path={`/${getPackageRoutes().BASE_ROUTE}/*`}
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Package}>
                          <Routes>
                            <Route
                              path="confirmation/:reservationId"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <PackageConfirmationPageStandalone />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              element={<Homepage funnel={Funnel.Package} />}
                            />
                            <Route
                              path="*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <PackagesApp />
                                </Suspense>
                              }
                            />
                          </Routes>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}

                  {/* Activities Routes */}
                  {isActivitiesEnabled && (
                    <Route
                      path={`${ACTIVITIES_PATH}/*`}
                      element={
                        <FunnelMaintenanceGuard funnel={Funnel.Activities}>
                          <Routes>
                            <Route
                              path="confirmation/:reservationId"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <ActivityConfirmationPageStandalone />
                                </Suspense>
                              }
                            />
                            <Route
                              index
                              element={<Homepage funnel={Funnel.Activities} />}
                            />
                            <Route
                              path="*"
                              element={
                                <Suspense fallback={<LazyLoading />}>
                                  <ActivitiesApp />
                                </Suspense>
                              }
                            />
                          </Routes>
                        </FunnelMaintenanceGuard>
                      }
                    />
                  )}

                  {/* Trips Routes */}
                  {isTripsEnabled && (
                    <>
                      {/* IMPORTANT: Exchange route must come BEFORE /trips/* catch-all to ensure proper routing */}
                      <Route
                        key={"flights-exchange"}
                        path={"/trips/flights/:itineraryId/exchange/*"}
                        element={
                          <Suspense fallback={<LazyLoading />}>
                            <FlightsExchangePage />
                          </Suspense>
                        }
                      />
                      <Route
                        key="trips"
                        path={"/trips/*"}
                        element={
                          <Suspense fallback={<LazyLoading />}>
                            <TripsApp />
                          </Suspense>
                        }
                      />
                    </>
                  )}

                  {/* Redirect disabled funnels to home */}
                  {!isFlightsEnabled && (
                    <Route
                      path="/flights/*"
                      element={<Navigate to="/" replace />}
                    />
                  )}
                  {!isStaysEnabled && (
                    <>
                      <Route
                        path={`${HOTELS_PATH}/*`}
                        element={<Navigate to="/" replace />}
                      />
                      <Route
                        path={`${HOMES_PATH}/*`}
                        element={<Navigate to="/" replace />}
                      />
                    </>
                  )}
                  {!isCarsEnabled && (
                    <Route
                      path="/cars/*"
                      element={<Navigate to="/" replace />}
                    />
                  )}
                  {!isPackagesEnabled && (
                    <Route
                      path={`/${getPackageRoutes().BASE_ROUTE}/*`}
                      element={<Navigate to="/" replace />}
                    />
                  )}
                  {!isActivitiesEnabled && (
                    <Route
                      path={`${ACTIVITIES_PATH}/*`}
                      element={<Navigate to="/" replace />}
                    />
                  )}
                  {!isTripsEnabled && (
                    <Route
                      path="/trips/*"
                      element={<Navigate to="/" replace />}
                    />
                  )}

                  <Route
                    path={`${WALLET_PATH}/*`}
                    element={
                      <Suspense fallback={<LazyLoading />}>
                        <WalletApp />
                      </Suspense>
                    }
                  />

                  {/* Travel wallet (offers + credits) */}
                  <Route
                    key={"travel-offers"}
                    path={TRAVEL_OFFERS_PATH}
                    element={<TravelOffersPage />}
                  />

                  {/* All travel deals: sponsored HTS Media tiles + funded hotel deals */}
                  <Route
                    key={"travel-deals"}
                    path={TRAVEL_DEALS_PATH}
                    element={<TravelDealsPage />}
                  />

                  {/* Hotel funded offers results page. Renders HotelCard
                      outside the core hotels route tree, so it needs its own
                      slot provider for HotelCardPrice. */}
                  <Route
                    key={"hotel-funded-offers"}
                    path={HOTEL_FUNDED_OFFERS_PATH}
                    element={
                      <DefaultHotelSlotProvider>
                        <HotelFundedOffersPage />
                      </DefaultHotelSlotProvider>
                    }
                  />

                  {/* Watches Routes */}
                  <Route
                    key={"home-watches"}
                    path={WATCHES_PATH}
                    element={
                      <Suspense fallback={<LazyLoading />}>
                        <WatchesApp />
                      </Suspense>
                    }
                  />
                  <Route
                    key={"watches"}
                    path={`${WATCHES_PATH}/*`}
                    element={
                      <Suspense fallback={<LazyLoading />}>
                        <WatchesApp />
                      </Suspense>
                    }
                  />

                  {/* Catch-all: redirect unknown paths to homepage */}
                  <Route path="*" element={<Navigate to="/" replace />} />
                </Routes>
              )}
              {isSignedIn && (
                <>
                  <HelpButton />
                  <HtsAssistChatWidgetGate />
                  <Outlet />
                </>
              )}
            </Layout>
          </ProtectedRoute>
        }
      />
    </Routes>
  );
};

const MainApp = () => {
  const { language } = useI18nContext();
  const [sessionInfo, setSessionInfo] = useState<SessionInfo | null>(null);
  const [pointsBalance, setPointsBalance] = useState<number | null>(0);
  const [error, _setError] = useState<string>("");

  const updateSessionInfo = useCallback((sessionInfo: SessionInfo) => {
    setSessionInfo(sessionInfo);
  }, []);

  const setError = useCallback((error: string) => {
    _setError(error);
  }, []);

  const isSignedIn = sessionInfo?.userScope?.isSignedIn;

  // Check for maintenance mode
  const { title, subtitle } = getMaintenanceMessages(language);

  const maintenanceProps = getMaintenanceProps({
    timedMaintenanceModeStartTime:
      environmentConfig.timedMaintenanceModeStartTime,
    timedMaintenanceModeEndTime: environmentConfig.timedMaintenanceModeEndTime,
    timedMaintenanceModeEnabled: environmentConfig.timedMaintenanceModeEnabled,
    preAuthMaintenanceModeEnabled: environmentConfig.preAuthMaintenanceEnabled,
    maintenanceTitle: title,
    maintenanceSubtitle: subtitle,
  });

  if (maintenanceProps.enabled) {
    /* pre-auth maintenance page */
    return (
      <Maintenance
        title={maintenanceProps.title}
        subtitle={maintenanceProps.subtitle}
      />
    );
  }

  return (
    <>
      <DocumentTitleUpdater />
      <APIProvider apiKey={import.meta.env.VITE_REACT_APP_GOOGLE_MAPS_API_KEY}>
        <UserContext.Provider
          value={{
            sessionInfo,
            updateSessionInfo,
            pointsBalance,
            updatePointsBalance: setPointsBalance,
            setError,
            error,
          }}
        >
          <AppTrackingProvider>
            <AxiosInterceptorWrapper version={environmentConfig.version}>
              <HtsAssistChatSupportProvider>
                <HotelsConfigProvider config={hotelAppConfig}>
                  <PageViewIdProvider>
                    <HtsMediaRequestMetadataProvider
                      channelId={HTS_MEDIA_CHANNEL_ID}
                    >
                      {/* Sends the user home if a route chunk fails to load,
                          rather than white-screening the app. */}
                      <ErrorBoundary>
                        <AppContent isSignedIn={isSignedIn} />
                      </ErrorBoundary>
                    </HtsMediaRequestMetadataProvider>
                  </PageViewIdProvider>
                </HotelsConfigProvider>
              </HtsAssistChatSupportProvider>
            </AxiosInterceptorWrapper>
          </AppTrackingProvider>
        </UserContext.Provider>
      </APIProvider>
    </>
  );
};

export const App = () => {
  // Fix scroll issues in mobile app when keyboard appears
  useMobileAppScrollFix();

  return (
    <Portal config={config} store={store}>
      <MainApp />
    </Portal>
  );
};
